import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "@emotion/react";
import { Flex } from "antd";
import Image from "@components/item/Image";
import useDropdownStore from "@store/useDropdownStore";
import { useApiRequest } from "@utils/ApiRequest";

const Container = styled.div({
  position: "relative",
  width: "100%",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string; placeholder: string }>(
  ({ theme, selectedValue, placeholder }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === placeholder
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  fontSize: "1.6rem",
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
  "::-webkit-scrollbar": {
    display: "none",
  },
}));

const OptionItem = styled.button(({ theme }) => ({
  width: "100%",
  padding: "10px 20px",
  fontSize: "1.6rem",
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));

type BankSelectorProps = {
  name: string;
  placeholder?: string;
};

const BankSelector: React.FC<BankSelectorProps> = ({
  name,
  placeholder = "선택",
}) => {
  const theme = useTheme();
  const [selectedValue, setSelectedValue] = useState(placeholder);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { dropdown, setDropdown } = useDropdownStore();
  const { setValue, watch } = useFormContext();
  const { getBankList } = useApiRequest();
  const language = localStorage.getItem("language");

  const { data: bankList = [] } = useQuery({
    queryKey: ["bankList"],
    queryFn: getBankList,
  });

  const handleChange = (value: string, bank_name: string) => {
    setSelectedValue(bank_name);
    setValue(name, value);
    setDropdown("");
  };

  const bankName = watch(name);

  useEffect(() => {
    if (bankList.length > 0) {
      const bank = bankList.find((bank: any) => bank.code === bankName);
      if (bank) {
        setSelectedValue(language === "en" ? bank.name_en : bank.name_ko);
      }
    }
  }, [bankList, language, bankName]);

  return (
    <Container>
      <Button
        onClick={() => setDropdown(name === dropdown ? "" : name)}
        type="button"
        selectedValue={selectedValue}
        placeholder={placeholder}
        ref={buttonRef}>
        <Flex gap={8}>{selectedValue}</Flex>
        <Image
          src={
            name === dropdown
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>

      {name === dropdown && (
        <SelectBox
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.1 }}>
          {bankList.map((bank: any) => (
            <OptionItem
              key={bank.code}
              onClick={() =>
                handleChange(
                  bank.code,
                  language === "en" ? bank.name_en : bank.name_ko,
                )
              }>
              {language === "en" ? bank.name_en : bank.name_ko}
            </OptionItem>
          ))}
        </SelectBox>
      )}
    </Container>
  );
};

export default BankSelector;
